import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { apiCall, ApiError } from '@/lib/api';
import { LoginResponseSchema } from '@/lib/schemas/user';

const PASSWORD_MIN_LENGTH = 10;

export function SetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    password?: string;
    confirmPassword?: string;
  }>({});
  const [success, setSuccess] = useState(false);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing password reset token.');
    }
  }, [token]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    if (!token) {
      setError('Password reset token is missing.');
      return;
    }

    const fe: { password?: string; confirmPassword?: string } = {};

    if (password.length < PASSWORD_MIN_LENGTH) {
      fe.password = `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`;
    }

    if (password !== confirmPassword) {
      fe.confirmPassword = 'Passwords do not match.';
    }

    if (Object.keys(fe).length > 0) {
      setFieldErrors(fe);
      return;
    }

    setBusy(true);
    try {
      const res = await apiCall<unknown>('/auth/set-password-from-token', {
        method: 'POST',
        body: { token, newPassword: password },
      });

      const parsed = LoginResponseSchema.safeParse(res.data);
      if (!parsed.success) {
        setError('Failed to process password setup. Please try again.');
        return;
      }

      // Store tokens and refresh page to trigger auth context update via GET /auth/me
      setSuccess(true);

      // Small delay to show success message, then redirect to dashboard
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.errorCode === 'TOKEN_EXPIRED') {
          setError(
            'This password reset link has expired. Please request a new one using "Forgot Password" on the login page.',
          );
        } else if (err.errorCode === 'INVALID_TOKEN') {
          setError(
            'This password reset link is invalid or has already been used.',
          );
        } else {
          setError('Failed to set password. Please try again.');
        }
      } else {
        setError('Network error. Please check your connection and try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-600 to-violet-600 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Invalid Link</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                This password reset link is invalid or has expired. Please go
                back to the login page and use "Forgot Password" to request a
                new link.
              </AlertDescription>
            </Alert>
            <Button
              onClick={() => navigate('/login')}
              className="w-full"
              variant="default"
            >
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-600 to-violet-600 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Set Your Password</CardTitle>
          <CardDescription>
            Create a secure password to access your CosmoBud account.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert variant="success" className="mb-4">
              <AlertTitle>Password Set Successfully</AlertTitle>
              <AlertDescription>
                Welcome to CosmoBud! You're being redirected to your dashboard…
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
                aria-invalid={fieldErrors.password ? true : undefined}
                placeholder="At least 10 characters"
              />
              {fieldErrors.password && (
                <p className="text-xs text-red-600">{fieldErrors.password}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <PasswordInput
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={busy}
                aria-invalid={fieldErrors.confirmPassword ? true : undefined}
                placeholder="Re-enter your password"
              />
              {fieldErrors.confirmPassword && (
                <p className="text-xs text-red-600">
                  {fieldErrors.confirmPassword}
                </p>
              )}
            </div>

            <Button type="submit" disabled={busy || success} className="w-full">
              {busy ? 'Setting password…' : 'Set Password'}
            </Button>
          </form>

          <p className="mt-4 text-center text-sm text-slate-600 dark:text-slate-400">
            After setting your password, you'll be able to log in and start
            exploring tutors.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
